
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ResultCard from '../search-form/results/ResultCard';
import FlightMultiResultCard from '../search-form/results/FlightMultiResultCard';
import FlightTrainResultCard from '../search-form/results/FlightTrainResultCard';
import RouteMapModal from '../modal/RouteMapModal';
import ShareMapModal from '../modal/ShareMapModal';
import { CardContactStrip } from '../contact-lead';

const toTitleCase = (str) => str ? str.replace(/\b\w/g, c => c.toUpperCase()) : str;

const formatPrice = (amount) => {
    const val = parseFloat(typeof amount === 'string' ? amount.replace(/,/g, '') : amount);
    if (isNaN(val) || !amount) return null;
    return val.toLocaleString('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    });
};

const TravelResultsList = ({
    routes,
    onBookClick,
    onBookBus,
    onShowError,
    searchMetadata,
    // Contact lead A/B, arm "card_button" (passed from SearchForm)
    showContactButton = false,
    onContactClick,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [mapModalState, setMapModalState] = useState({
        isOpen: false,
        route: null,
        title: ''
    });
    const [shareModalState, setShareModalState] = useState({
        isOpen: false,
        route: null
    });

    // Robust Auto-Open Map Logic for Shared Links
    useEffect(() => {
        const shouldOpenMap = searchParams.get('openMap') === 'true';

        if (shouldOpenMap && routes && routes.length > 0 && !mapModalState.isOpen) {
            // Delay slightly to ensure smooth transition after results appear
            const timer = setTimeout(() => {
                const firstRoute = routes[0];
                const cardProps = mapRouteToCardProps(firstRoute);
                const title = cardProps.props.segment
                    ? `Route: ${cardProps.props.segment.origin_name} to ${cardProps.props.segment.dest_name}`
                    : `Route: ${cardProps.props.origin.name} to ${cardProps.props.destination.name}`;

                handleViewMap(firstRoute, title);

                // Clean up URL: Remove openMap param so it doesn't re-trigger on filters/re-renders
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('openMap');
                setSearchParams(newParams, { replace: true });
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [routes, searchParams]);

    if (!routes || routes.length === 0) return null;

    const handleViewMap = (route, title) => {
        setMapModalState({
            isOpen: true,
            route,
            title
        });
    };

    const handleShare = (route) => { setShareModalState({ isOpen: true, route }); };

    const closeMapModal = () => {
        setMapModalState(prev => ({ ...prev, isOpen: false }));
    };

    const closeShareModal = () => { setShareModalState(prev => ({ ...prev, isOpen: false })); };

    const mapRouteToCardProps = (route) => {
        const nodes = route.nodes || [];
        const isMultiSegment = nodes.length > 1;

        // Determine Route Type
        let hasFlight = false;
        let hasTrain = false;
        let hasBus = false;
        const routeType = route.route_type;

        if (routeType === 1) {
            hasTrain = true;
        } else if (routeType === 2) {
            hasFlight = true;
        } else if (routeType === 3) {
            hasBus = true;
        } else if (routeType === 4) {
            hasTrain = true;
            hasFlight = true;
        } else if (routeType === 5) { // MIXED_FB = Bus+Flight
            hasBus = true;
            hasFlight = true;
        } else {
            nodes.forEach(n => {
                const seg = n.segment || {};
                if (seg.flight_num || (seg.carrier && !seg.train_name)) hasFlight = true;
                if (seg.train_name || seg.train_num) hasTrain = true;
                if (seg.bus_type || (seg.travel_name && !seg.train_name && !seg.flight_num)) hasBus = true;
            });
        }

        const getRootTotalFare = (r) => {
            if (r.total_fare !== null && r.total_fare !== undefined) return r.total_fare;
            let sum = 0;
            let foundAny = false;
            r.nodes?.forEach(n => {
                const f = n.segment?.seat_avail_ui?.fare || n.segment?.fare;
                if (f !== null && f !== undefined) {
                    sum += parseFloat(f);
                    foundAny = true;
                }
            });
            return foundAny ? sum : null;
        };

        const calculatedTotalFare = getRootTotalFare(route);

        // Single Segment Card (Train, Flight or Bus)
        if (!isMultiSegment) {
            const segment = nodes[0]?.segment;
            let type = 'train';
            if (hasFlight) type = 'flight';
            else if (hasBus) type = 'bus';

            return {
                Component: ResultCard,
                props: {
                    type: type,
                    tags: route.tags || [],
                    flat_off_amount_flight: route.flat_off_amount_flight || null,
                    flat_off_flight_promo: route.flat_off_flight_promo || null,
                    flat_off_flight_promo_desc: route.flat_off_flight_promo_desc || null,
                flat_off_amount_bus: route.flat_off_amount_bus || null,
                flat_off_bus_promo: route.flat_off_bus_promo || null,
                flat_off_bus_promo_desc: route.flat_off_bus_promo_desc || null,
                    segment: {
                        ...segment,
                        mode: type,
                        // Pass raw fields for ResultCard to format
                        origin_name: segment?.origin_name,
                        origin_code: segment?.origin_code_iata || segment?.origin_code || (type === 'bus' && segment?.origin_name ? segment.origin_name.substring(0, 3).toUpperCase() : ''),
                        origin_dep_dt: segment?.origin_dep_dt,
                        dest_name: segment?.dest_name,
                        dest_code: segment?.dest_code_iata || segment?.dest_code || (type === 'bus' && segment?.dest_name ? segment.dest_name.substring(0, 3).toUpperCase() : ''),
                        dest_arr_dt: segment?.dest_arr_dt,
                        flight_num: (() => {
                            const num  = segment?.flight_num || segment?.train_num || segment?.service_name || '';
                            const name = segment?.carrier || segment?.train_name || segment?.travel_name || '';
                            // For bus: if service_name and travel_name are the same (case-insensitive), show only one
                            if (type === 'bus' && num && name &&
                                num.trim().toLowerCase() === name.trim().toLowerCase()) return '';
                            return num;
                        })(),
                        flight_name: (() => {
                            const isBusSeg2 = !!(segment?.bus_type || (segment?.travel_name && !segment?.train_name && !segment?.flight_num));
                            const raw = segment?.carrier || segment?.train_name || segment?.travel_name || '';
                            return isBusSeg2 && raw.length > 22 ? raw.substring(0, 22).trim() + '…' : raw;
                        })(),
                        // Pass seat info from segment to top level for easy access in generic card
                        seat_class_ui: segment?.seat_class_ui || (type === 'bus' ? (segment?.bus_type || segment?.seat_avail_ui?.class || '') : ''),
                        seat_avail_ui: segment?.seat_avail_ui,
                        category: segment?.category || (type === 'bus' ? 'Bus' : ''),
                        booking_link: segment?.booking_link
                    },
                    price: formatPrice(
                        calculatedTotalFare ??
                        (hasBus
                            ? (segment?.seat_avail_ui?.General?.fare || segment?.fare)
                            : null)
                    ),
                    duration: segment?.duration_mins ? `${Math.floor(segment.duration_mins / 60)}h ${segment.duration_mins % 60}m` : 'N/A',
                    stops: 0,
                    seatStatus: (() => {
                        if (hasBus) {
                            // Bus: use General.available from seat_avail_ui
                            const avail = segment?.seat_avail_ui?.General?.available;
                            if (avail != null && avail > 0) return `${avail} seats available`;
                            if (avail === 0) return 'Sold out';
                            return 'Seats available'; // available if returned by API
                        }
                        const isAvailable = segment?.seat_avail_ui?.is_avail ?? false;
                        return isAvailable ? "Seats available" : "Seats not available";
                    })(),
                    originCommentWeb: segment?.origin_comment_web || null,
                    originCommentWebTitle: segment?.origin_comment_web_title || null,
                    destCommentWeb: segment?.dest_comment_web || null,
                    destCommentWebTitle: segment?.dest_comment_web_title || null,
                    originCab: segment?.origin_cab || null,
                    destCab: segment?.dest_cab || null,
                    explanation: route.explanation || null
                }
            };
        }

        // Multi Segment Logic
        const legs = [];
        nodes.forEach((node, index) => {
            const seg = node.segment || {};
            const isFlight = !!(seg.flight_num || (seg.carrier && !seg.train_name));
            const isTrain = !!(seg.train_name || seg.train_num);
            const isBus = !!(seg.bus_type || (seg.travel_name && !seg.train_name && !seg.flight_num));

            // Segment Leg
            legs.push({
                type: isFlight ? 'flight' : isTrain ? 'train' : isBus ? 'bus' : 'layover',
                number: seg.flight_num || seg.train_num || seg.service_name,
                name: seg.carrier || seg.train_name || seg.travel_name,
                // Preserve promo exactly from the segment object. Bus promos are
                // supplied by the bus segment under flat_off_flight_promo_desc.
                flat_off_flight_promo_desc: seg.flat_off_flight_promo_desc || null,
                origin: {
                    name: isBus ? toTitleCase(seg.origin_name) : seg.origin_name,
                    code: seg.origin_code_iata || seg.origin_code || (isBus && seg.origin_name ? seg.origin_name.substring(0, 3).toUpperCase() : ''),
                    time: seg.origin_dep_dt ? new Date(seg.origin_dep_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
                    date: seg.origin_dep_dt ? new Date(seg.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '',
                    dep_dt: seg.origin_dep_dt // Raw timestamp for refresh
                },
                destination: {
                    name: isBus ? toTitleCase(seg.dest_name) : seg.dest_name,
                    code: seg.dest_code_iata || seg.dest_code || (isBus && seg.dest_name ? seg.dest_name.substring(0, 3).toUpperCase() : ''),
                    time: seg.dest_arr_dt ? new Date(seg.dest_arr_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
                    date: seg.dest_arr_dt ? new Date(seg.dest_arr_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
                },
                duration: `${Math.floor(seg.duration_mins / 60)}h ${seg.duration_mins % 60}m`,
                fare: seg.seat_avail_ui?.fare || seg.seat_avail_ui?.General?.fare || seg.fare || null,
                price: formatPrice(seg.seat_avail_ui?.fare || seg.seat_avail_ui?.General?.fare || seg.fare),
                description: seg.origin_comment || seg.dest_comment || "Layover",
                avail: seg.seat_avail_ui?.avl || "Avail",
                class: seg.seat_class_ui || "E",
                halts: seg.stop_count || 0,
                footerText: seg.seat_avail_ui?.explanation || null,
                seat_avail_ui: seg.seat_avail_ui, // Full object for fallback
                seat_class_ui: seg.seat_class_ui, // Current class ID
                seat_classes: seg.seat_classes || [],
                seat_avail: seg.seat_avail || {},
                seat_costs: seg.seat_costs || {},
                category: seg.category || '',
                // Commute info
                originCommentWeb: seg.origin_comment_web || null,
                originCommentWebTitle: seg.origin_comment_web_title || null,
                destCommentWeb: seg.dest_comment_web || null,
                destCommentWebTitle: seg.dest_comment_web_title || null,
                booking_link: seg.booking_link,
                trace_id: seg.trace_id,
                result_idx: seg.result_idx,
                is_lcc: seg.is_lcc,
                valid_till_ts: seg.valid_till_ts,
                baggage: seg.baggage,
                raw_segment: isBus ? seg : null
            });


            // Intermediate Layover from stops_data if present
            if (index < nodes.length - 1 && route.stops_data && route.stops_data[index]) {
                const stop = route.stops_data[index];

                // Show only commute_comment if available to keep it clean. 
                // Fallback to timing explanation ONLY if no commute info exists.
                const explanation = stop.commute_comment || stop.exp_time_avl_explanation || stop.exp_time_avl_comment;

                if (explanation || stop.layover_cab) {
                    legs.push({
                        type: 'layover',
                        title: stop.stop_title || "Layover",
                        location: stop.arr_at_name || stop.dep_from_name || "Connection",
                        description: explanation || '',
                        layoverCab: stop.layover_cab || null
                    });
                }
            }
        });

        const firstSeg = nodes[0].segment;
        const lastSeg = nodes[nodes.length - 1].segment;
        const busSegment = nodes
            .map(n => n?.segment)
            .find(seg => {
                const type = String(seg?.mode || seg?.type || '').toLowerCase();
                return type === 'bus' || type.includes('bus') || !!seg?.bus_type || (
                    !!seg?.travel_name &&
                    !seg?.flight_num &&
                    !seg?.train_num &&
                    !seg?.train_name &&
                    !seg?.carrier
                );
            });
        const busPromoDesc = busSegment?.flat_off_flight_promo_desc || null;
        const totalStops = nodes.length - 1;

        const commonProps = {
            legs: legs,
            tags: route.tags || [],
            flat_off_amount_flight: route.flat_off_amount_flight || null,
            flat_off_flight_promo: route.flat_off_flight_promo || null,
            flat_off_flight_promo_desc: route.flat_off_flight_promo_desc || null,
            // Bus promo is intentionally sourced from the BUS segment, not route-level flight promo.
            flat_off_bus_promo_desc: busPromoDesc,
            destCommentWeb: lastSeg.dest_comment_web || null,
            destCommentWebTitle: lastSeg.dest_comment_web_title || null,
            originCab: firstSeg.origin_cab || null,
            destCab: lastSeg.dest_cab || null,
            totalDuration: (() => {
                let totalMins = 0;
                let hasValidData = true;

                nodes.forEach((node, index) => {
                    const seg = node.segment || {};
                    const dur = parseInt(seg.duration_mins);
                    if (!isNaN(dur)) {
                        totalMins += dur;
                    } else {
                        hasValidData = false;
                    }

                    // Add layover duration if not the last segment
                    if (index < nodes.length - 1) {
                        const nextSeg = nodes[index + 1].segment || {};
                        const arrivalTime = new Date(seg.dest_arr_dt);
                        const departureTime = new Date(nextSeg.origin_dep_dt);

                        if (!isNaN(arrivalTime.getTime()) && !isNaN(departureTime.getTime())) {
                            const diffMins = Math.floor((departureTime - arrivalTime) / (1000 * 60));
                            // Only add if it's a positive layover (safety check)
                            if (diffMins > 0) {
                                totalMins += diffMins;
                            }
                        } else {
                            hasValidData = false;
                        }
                    }
                });

                if (hasValidData && totalMins > 0) {
                    return `${Math.floor(totalMins / 60)}h ${totalMins % 60}m`;
                }

                // Fallback: Original clock subtraction if segment data is missing
                const diffTime = new Date(lastSeg.dest_arr_dt) - new Date(firstSeg.origin_dep_dt);
                return `${Math.floor(diffTime / (1000 * 60 * 60))}h ${Math.floor((diffTime / (1000 * 60)) % 60)}m`;
            })(),
            price: formatPrice(calculatedTotalFare),
            origin: {
                name: (hasBus && !hasTrain) ? toTitleCase(firstSeg.origin_name) : firstSeg.origin_name,
                code: firstSeg.origin_code_iata || firstSeg.origin_code || (firstSeg.origin_name ? firstSeg.origin_name.substring(0, 3).toUpperCase() : ''),
                time: new Date(firstSeg.origin_dep_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                date: new Date(firstSeg.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
            },
            destination: {
                name: (hasBus && !hasTrain) ? toTitleCase(lastSeg.dest_name) : lastSeg.dest_name,
                code: lastSeg.dest_code_iata || lastSeg.dest_code || (lastSeg.dest_name ? lastSeg.dest_name.substring(0, 3).toUpperCase() : ''),
                time: new Date(lastSeg.dest_arr_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                date: new Date(lastSeg.dest_arr_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
            },
            fareOptions: [{ class: "Economy", avail: "Available", price: formatPrice(calculatedTotalFare) }],
            stops: totalStops,
            halts: totalStops, // For mixed/train cards
            explanation: route.explanation || null,
            seatStatus: (() => {
                const transportNodes = nodes.filter(n => n.segment);

                const availList = transportNodes.map(n => {
                    const seg = n.segment || {};
                    const isBusSeg = !!(seg.bus_type || (seg.travel_name && !seg.train_name && !seg.flight_num));
                    if (isBusSeg) {
                        // Bus: available if General.available > 0
                        const avail = seg?.seat_avail_ui?.General?.available;
                        return avail != null ? avail > 0 : true;
                    }
                    return seg?.seat_avail_ui?.is_avail === true;
                });

                if (availList.length === 0) return null;

                const allAvail = availList.every(v => v === true);
                const someAvail = availList.some(v => v === true);

                let finalText;
                if (allAvail) finalText = "Seats available";
                else if (someAvail) finalText = "Seats partially available";
                else finalText = "Seats not available";

                // 🔍 DEBUG: route-level summary if train present
                const has22440 = transportNodes.some(
                    n => n.segment?.train_num === "22440"
                );

                if (has22440) {
                    console.log("[SEAT DEBUG 22440 - ROUTE]", {
                        availList,
                        finalText
                    });
                }

                return finalText;
            })()
        };

        if (hasTrain && hasFlight) {
            return {
                Component: FlightTrainResultCard,
                props: {
                    ...commonProps,
                    routeInfo: "Multiple Mode Route"
                }
            };
        } else {
            // Determine secondary mode info 
            let mode = 'flight';
            if (hasTrain) mode = 'train';
            else if (hasBus && hasFlight) mode = 'mixed-bf';
            else if (hasBus) mode = 'bus';

            return {
                Component: FlightMultiResultCard,
                props: {
                    ...commonProps,
                    mode: mode,
                    airlineInfo: nodes.map(n => {
                        const seg = n.segment || {};
                        const isBusSeg = !!(seg.bus_type || (seg.travel_name && !seg.train_name && !seg.flight_num));
                        const raw = seg.carrier || seg.train_name || seg.travel_name || '';
                        return isBusSeg && raw.length > 22 ? raw.substring(0, 22).trim() + '…' : raw;
                    }).filter(Boolean).join(' + '),
                    modeOperators: nodes
                        .filter(n => n.segment && (n.segment.bus_type || n.segment.travel_name || n.segment.carrier || n.segment.train_name || n.segment.flight_num))
                        .map(n => {
                            const seg = n.segment;
                            const isBusSeg = !!(seg.bus_type || (seg.travel_name && !seg.train_name && !seg.flight_num));
                            const isFlightSeg = !!(seg.carrier || seg.flight_num || seg.airline_name);
                            const isTrainSeg = !!(seg.train_name || seg.train_num);
                            const rawName = seg.travel_name || seg.carrier || seg.train_name || seg.airline_name || '';
                            const truncName = isBusSeg && rawName.length > 22 ? rawName.substring(0, 22).trim() + '…' : rawName;
                            return {
                                type: isBusSeg ? 'bus' : isFlightSeg ? 'flight' : isTrainSeg ? 'train' : 'other',
                                name: truncName
                            };
                        })
                        .filter(m => m.type !== 'other' && m.name)
                }
            };
        }
    };

    return (
        <div className="space-y-6">
            {routes.map((route, index) => {
                const { Component, props } = mapRouteToCardProps(route);
                const cardTitle = props.segment
                    ? `Route: ${props.segment.origin_name} to ${props.segment.dest_name}`
                    : `Route: ${props.origin.name} to ${props.destination.name}`;

                return (
                    <div key={index} className="fade-in">
                        <Component
                            card={props}
                            onViewMap={() => handleViewMap(route, cardTitle)}
                            onShare={() => handleShare(route)}
                            onBookClick={onBookClick}
                            onBookBus={onBookBus}
                            onShowError={onShowError}
                        />
                        {showContactButton && onContactClick && (
                            <CardContactStrip onClick={() => onContactClick(route)} />
                        )}
                    </div>
                );
            })}

            <RouteMapModal
                isOpen={mapModalState.isOpen}
                onClose={closeMapModal}
                routeData={mapModalState.route}
                title={mapModalState.title}
                searchMetadata={searchMetadata}
            />

            <ShareMapModal
                isOpen={shareModalState.isOpen}
                onClose={closeShareModal}
                routeData={shareModalState.route}
                searchMetadata={searchMetadata}
            />
        </div>
    );
};

export default TravelResultsList;
